import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { userLoginSuccess } from 'redux/actions/login';
import { removeLocalStorageItemOnLogout } from 'utils/removeLocalStorageItemOnLogout';
import { IStonlyData } from 'types/StonlyData';
import { ITenant, IUser } from 'types';

interface UserContextType {
  setUser: (user: IUser | null) => void;
  user: IUser;
  logout: () => void;
  navLinkRef: React.RefObject<HTMLDivElement>;
  navref: HTMLDivElement | null;
  setNavref: (ref: HTMLDivElement | null) => void;
  rawSetUser: Dispatch<SetStateAction<IUser | null>>;
  stonlyData: IStonlyData | null;
  setStonlyData: React.Dispatch<React.SetStateAction<IStonlyData | null>>;
  tenantInformation: ITenant[] | null;
  setTenantInformation: (info: ITenant[]) => void;
}

export const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  children: React.ReactNode;
  userLoginSuccess: (user: IUser) => void;
}

function UserProvider({ children, userLoginSuccess }: UserProviderProps) {
  const [user, rawSetUser] = useState<IUser | null>(null);
  const [stonlyData, setStonlyData] = useState(null);
  const [navref, setNavref] = useState<HTMLDivElement | null>(null);
  const [tenantInformation, setTenantInformation] = useState<ITenant[] | null>(
    null,
  );
  const navLinkRef = React.useRef<HTMLDivElement>(null);

  const setUser = useCallback(
    (user: IUser | null) => {
      if (user) {
        userLoginSuccess(user as IUser);
        rawSetUser(user as IUser);
      } else {
        rawSetUser(null);
      }
    },
    [userLoginSuccess, rawSetUser],
  );

  const logout = useCallback(() => {
    // clear cookies and local storage
    document.cookie =
      'md6_var_profile=; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=varicontest.com.au;';
    removeLocalStorageItemOnLogout();
  }, []);

  const contextValue = useMemo(
    () => ({
      setUser,
      user,
      logout,
      navLinkRef,
      navref,
      setNavref,
      rawSetUser,
      stonlyData,
      setStonlyData,
      tenantInformation,
      setTenantInformation,
    }),
    [
      setUser,
      user,
      logout,
      navLinkRef,
      navref,
      setNavref,
      rawSetUser,
      stonlyData,
      setStonlyData,
      tenantInformation,
      setTenantInformation,
    ],
  );

  return (
    <UserContext.Provider value={contextValue as UserContextType}>
      {children}
    </UserContext.Provider>
  );
}

export default connect(null, { userLoginSuccess })(UserProvider);
